import gsap from "gsap";

const isTouchDevice = 'ontouchstart' in window;

const getCursorElements = () => ({
  cursor: document.querySelector('[data-cursor]'),
  cursorArrow: document.querySelectorAll('[data-cursor-arrow]'),
  cursorArrowBlob: document.querySelectorAll('[data-cursor-arrow-blob]'),
  cursorExpand: document.querySelectorAll('[data-cursor-expand]'),
  cursorExpandBlob: document.querySelectorAll('[data-cursor-expand-blob]'),
  cursorPlay: document.querySelectorAll('[data-cursor-play]'),
  cursorPlayBlob: document.querySelectorAll('[data-cursor-play-blob]'),
  cursorText: document.querySelectorAll('[data-cursor-text]'),
  cursorTextBlob: document.querySelectorAll('[data-cursor-text-blob]'),
});

let elements = getCursorElements();

const hideCursorElementsForTouch = () => {
  if (!isTouchDevice) return false;

  const { cursor, cursorArrowBlob, cursorExpandBlob, cursorPlayBlob, cursorTextBlob } = elements;

  if (cursor) cursor.style.display = 'none';
  [cursorArrowBlob, cursorExpandBlob, cursorPlayBlob, cursorTextBlob].forEach(collection => {
    collection.forEach(el => { el.style.display = 'none'; });
  });

  return true;
};

const handleSiteCursorEnter = (e) => {
  const { target, x, y } = e;
  const { cursor } = elements;

  const ignoreTargets = ['a', 'button', '[data-cursor-expand]', '[data-cursor-play]', '[data-cursor-text]', '.edd-root'];
  const ignoreCursor = ignoreTargets.some(selector => target?.closest(selector));

  gsap.to(cursor, {
    x: x - 7,
    y: y - 7,
    duration: 1.2,
    ease: 'power4.out',
    transform: `scale(${ignoreCursor ? 0 : 1})`,
  });
};

const handleSiteCursorLeave = () => {
  gsap.to(elements.cursor, {
    scale: 0,
    duration: 0.5,
    ease: 'power4.out',
  });
};

const initializeCustomCursor = (cursors, blobSelector) => {
  cursors.forEach((el) => {
    const cursorBlob = el.querySelector(blobSelector);

    if (!cursorBlob) return;

    gsap.set(cursorBlob, { scale: 0 });

    el.addEventListener('mouseenter', (e) => {
      const { clientX: x, clientY: y } = e;
      gsap.to(cursorBlob, {
        x: x - el.getBoundingClientRect().left,
        y: y - el.getBoundingClientRect().top,
        scale: 1,
        duration: 0.8,
        ease: 'power4.out',
      });
    });

    el.addEventListener('mousemove', (e) => {
      const { clientX: x, clientY: y } = e;
      gsap.to(cursorBlob, {
        x: x - el.getBoundingClientRect().left - cursorBlob.offsetWidth / 2,
        y: y - el.getBoundingClientRect().top - cursorBlob.offsetHeight / 2,
        duration: 0.8,
        ease: 'power4.out',
      });
    });

    el.addEventListener('mouseleave', () => {
      gsap.to(cursorBlob, {
        scale: 0,
        duration: 0.8,
        ease: 'power4.out',
      });
    });
  });
};

const initializeAllCustomCursors = () => {
  const { cursorArrow, cursorExpand, cursorPlay, cursorText } = elements;

  initializeCustomCursor(cursorArrow, '[data-cursor-arrow-blob]');
  initializeCustomCursor(cursorExpand, '[data-cursor-expand-blob]');
  initializeCustomCursor(cursorPlay, '[data-cursor-play-blob]');
  initializeCustomCursor(cursorText, '[data-cursor-text-blob]');
};

const observeNewCursorElements = () => {
  const targetNode = document.body;

  const config = {
    childList: true,
    subtree: true,
  };

  const observerCallback = (mutationsList) => {
    for (const mutation of mutationsList) {
      if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.hasAttribute('data-cursor-expand')) {
              initializeCustomCursor([node], '[data-cursor-expand-blob]');
            } else {
              const newExpandElements = node.querySelectorAll('[data-cursor-expand]');
              initializeCustomCursor(newExpandElements, '[data-cursor-expand-blob]');
            }
          }
        });
      }
    }
  };

  const observer = new MutationObserver(observerCallback);
  observer.observe(targetNode, config);
};

const initCursor = () => {
  if (hideCursorElementsForTouch()) return;

  const { cursor } = elements;
  gsap.set(cursor, { scale: 0 });

  window.addEventListener('mousemove', handleSiteCursorEnter);
  document.addEventListener('mouseleave', handleSiteCursorLeave);

  initializeAllCustomCursors();
  observeNewCursorElements();
};

const reinitializeCursors = () => {
  elements = getCursorElements();

  if (hideCursorElementsForTouch()) return;

  initializeAllCustomCursors();
};

export default {
  init: initCursor,
  reinitializeCursors
};
